import React, { useEffect, useRef, useState } from "react";
import ArabicSentence from "./ArabicSentence";
import WordMorphology from "./WordMorphology";
import IrabUpsell from "./IrabUpsell";
import useArabicFieldRemap from "./typing/useArabicFieldRemap";
import useIrabAnalysis from "../hooks/useIrabAnalysis";
import { track } from "../utils/analytics";

// Shareable link for an analyzed sentence: /irab?text=… prepopulates and
// auto-analyzes for whoever opens it (their visit is a cache hit — the
// sharer's analysis already warmed it). Production links use the short
// muraja.app form (the landing vhost 301s it, query intact); anywhere else
// (dev, LAN) shares its own origin so the link still works.
function shareIrabUrl(sentence) {
  const base = window.location.hostname === "my.muraja.app"
    ? "https://muraja.app"
    : window.location.origin;
  return `${base}/irab?text=${encodeURIComponent(sentence)}`;
}

/**
 * IrabPanel — the Iʿrāb tool's working surface, shared by the in-app overlay
 * (IrabTool.js) and the public page (PublicIrabPage.js). The analysis
 * pipeline (analyze → refine → peek-poll, tier/quota/upsell handling) lives
 * in useIrabAnalysis, shared with the PDF reader's Iʿrāb mode; this
 * component owns only the textarea, pills, and rendering.
 *
 * viewOnly: the reader may look but not analyze — no textarea, no Analyze
 * button; the panel opens on initialText and shows whatever the cache
 * already holds (peek, no engine/AI/quota). Used by the Quranic topic's
 * per-ayah button for non-admins: admins analyze an ayah once, everyone
 * else views that analysis.
 */
const PILLS = [
  { key: "verb", label: "Verb", color: "var(--tag-violet)", bg: "var(--tag-violet-bg)" },
  { key: "noun", label: "Noun", color: "var(--tag-rose)", bg: "var(--tag-rose-bg)" },
  { key: "pronoun", label: "Pronoun", color: "var(--tag-teal)", bg: "var(--tag-teal-bg)" },
  { key: "prepositions", label: "Prepositions", color: "var(--tag-orange)", bg: "var(--tag-orange-bg)" },
];

export default function IrabPanel({ onClose, initialText = "", autoAnalyze = false, context = "overlay", viewOnly = false }) {
  const [text, setText] = useState(initialText);
  const [colorToggles, setColorToggles] = useState({
    verb: true, noun: true, pronoun: true, prepositions: true,
  });
  const [structureWord, setStructureWord] = useState(null); // { key, morph }
  const [shareState, setShareState] = useState("idle"); // idle | copied
  const inputRef = useRef(null);
  const autoRan = useRef(false);
  const shareResetRef = useRef(null);
  const { remapOn, toggleRemap, remapKeyDown } = useArabicFieldRemap();
  const {
    sentence, tokens, refining, loading, error, notice,
    upsell, upsellRetryAt, tier, remaining, refineRemaining,
    maxChars, subscriberDaily, isStaff, analyze, peek, verifyToken,
  } = useIrabAnalysis({ context });
  const [notAnalyzed, setNotAnalyzed] = useState(false);

  // Escape closes the hosting overlay — but only when the word-structure
  // modal isn't open (it has its own Escape-to-close).
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !structureWord) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, structureWord]);

  useEffect(() => {
    if (!viewOnly) inputRef.current?.focus();
  }, [viewOnly]);

  const submit = () => {
    if (viewOnly) return;
    analyze(text);
  };

  useEffect(() => () => clearTimeout(shareResetRef.current), []);

  // Share the analyzed sentence. Native share sheet ONLY on touch devices,
  // where it's the door into TikTok/WhatsApp; on desktop navigator.share
  // exists too (e.g. macOS Safari) but its sheet has no Copy option — and
  // copy is what desktop sharing means. Uses the backend-echoed NFC
  // sentence so the link is a byte-exact cache key for whoever opens it.
  const share = async () => {
    const url = shareIrabUrl(sentence);
    track("irab-share-click", { context });
    const touchDevice = window.matchMedia?.("(pointer: coarse)")?.matches;
    if (navigator.share && touchDevice) {
      try {
        await navigator.share({ title: "Iʿrāb — إعراب", url });
      } catch {
        // user dismissed the sheet — nothing else to do
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareState("copied");
      clearTimeout(shareResetRef.current);
      shareResetRef.current = setTimeout(() => setShareState("idle"), 2000);
    } catch {
      // Clipboard blocked (rare) — show the URL for manual copy.
      window.prompt("Copy this link:", url);
    }
  };

  // ?text= handoff (SEO page / shared links): the host page sets initialText
  // after this panel's first render, so sync it into the textarea too —
  // recipients should see the sentence in the box, ready to edit.
  useEffect(() => {
    if (initialText) setText((t) => t || initialText);
  }, [initialText]);

  // …and analyze once the panel is ready. In view-only mode this is a
  // cache read instead — nothing is ever triggered on the reader's behalf.
  useEffect(() => {
    if (autoAnalyze && initialText && !autoRan.current) {
      autoRan.current = true;
      if (viewOnly) {
        peek(initialText).then((found) => setNotAnalyzed(!found));
      } else {
        analyze(initialText);
      }
    }
  }, [autoAnalyze, initialText, analyze, peek, viewOnly]);

  const onInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
      return;
    }
    remapKeyDown(e, setText);
  };

  const counterBits = [];
  counterBits.push(`${text.length}/${maxChars}`);
  if (remaining != null) counterBits.push(`${remaining} sentences left today`);
  if (tier === "free" && refineRemaining != null) counterBits.push(`${refineRemaining} AI checks left`);

  return (
    <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 shrink-0" style={{ borderBottom: "1px solid var(--panel-line)" }}>
        <div className="flex items-center gap-2.5">
          <span className="text-xl select-none">◇</span>
          <div>
            <h2 className="text-base font-semibold text-dt-parchment leading-tight">Iʿrāb — إعراب</h2>
            <p className="text-xs text-dt-parchment-dim leading-tight">
              {viewOnly ? "Word-by-word structure of this ayah" : "Analyze any Arabic sentence, word by word"}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-dt-parchment-dim hover:text-dt-parchment text-xl leading-none p-1"
          >
            ✕
          </button>
        )}
      </div>

      {/* Input — hidden entirely in view-only mode (no way to trigger an analysis) */}
      {!viewOnly && (
      <div className="px-5 pt-4 shrink-0">
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, maxChars))}
          onKeyDown={onInputKeyDown}
          rows={2}
          dir="rtl"
          lang="ar"
          placeholder="اُكْتُبْ جُمْلَةً عَرَبِيَّةً هُنَا…"
          className="dt-field w-full resize-none px-3.5 py-2.5 rounded-xl font-amiri text-2xl leading-relaxed"
        />
        <div className="flex items-center justify-between gap-3 mt-2.5">
          <div className="flex items-center gap-3 text-xs text-dt-parchment-dim">
            <button
              onClick={toggleRemap}
              title="Type Arabic on a US keyboard (same layout as the typing lessons)"
              className={`px-2 py-1 rounded-lg border transition-colors ${remapOn ? "text-dt-gold-bright" : ""}`}
              style={{ borderColor: "var(--panel-line)" }}
            >
              ⌨ع {remapOn ? "on" : "off"}
            </button>
            <span>{counterBits.join(" · ")}</span>
          </div>
          <button
            onClick={submit}
            disabled={loading || !text.trim()}
            className="px-4 py-2 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-[filter]"
          >
            {loading ? "Analyzing…" : "Analyze — أَعْرِب"}
          </button>
        </div>
      </div>
      )}

      {/* Errors / notices */}
      {error && !upsell && (
        <div className="mx-5 mt-3 px-3.5 py-2.5 rounded-xl text-sm shrink-0"
          style={{ background: "var(--tag-rose-bg)", border: "1px solid var(--panel-line)", color: "var(--tag-rose)" }}>
          {error}
        </div>
      )}
      {notice && !error && (
        <div className="mx-5 mt-3 px-3.5 py-2.5 rounded-xl text-sm shrink-0"
          style={{ background: "var(--tag-orange-bg)", border: "1px solid var(--panel-line)", color: "var(--tag-orange)" }}>
          {notice}
        </div>
      )}
      {upsell && (
        <IrabUpsell kind={upsell} retryAt={upsellRetryAt} subscriberDaily={subscriberDaily} context={context} />
      )}

      {/* Result */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {tokens ? (
          <>
            {/* Color pills + share */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {PILLS.map((p) => {
                const on = colorToggles[p.key];
                return (
                  <button
                    key={p.key}
                    onClick={() => setColorToggles((t) => ({ ...t, [p.key]: !t[p.key] }))}
                    className="px-2.5 py-1 rounded-full text-xs border transition-colors"
                    style={on
                      ? { color: p.color, background: p.bg, borderColor: "transparent" }
                      : { color: "var(--parchment-dim)", borderColor: "var(--panel-line)" }}
                  >
                    {p.label}
                  </button>
                );
              })}
              <button
                onClick={share}
                title="Copy a link that opens this sentence, analyzed"
                className="ms-auto px-2.5 py-1 rounded-full text-xs border transition-colors hover:text-dt-gold-bright"
                style={{
                  color: shareState === "copied" ? "var(--gold-bright)" : "var(--parchment-dim)",
                  borderColor: shareState === "copied" ? "var(--gold)" : "var(--panel-line)",
                }}
              >
                {shareState === "copied" ? "Link copied ✓" : "⤴ Share"}
              </button>
            </div>

            <ArabicSentence
              text={sentence}
              tokens={tokens}
              colorToggles={colorToggles}
              refiningIndices={refining}
              onTokenClick={(key, morph) => setStructureWord({ key, morph })}
            />

            <p className="text-xs text-dt-parchment-dim mt-5">
              Tap a word for its structure and iʿrāb.
              {refining.size > 0 && " Words with a spinner are being checked by the AI…"}
            </p>
          </>
        ) : viewOnly ? (
          loading ? (
            <div className="text-center text-sm text-dt-parchment-dim py-10">Loading…</div>
          ) : notAnalyzed && !error ? (
            <div className="text-center py-10">
              <p className="font-amiri text-2xl text-dt-parchment mb-3" dir="rtl" lang="ar">{initialText}</p>
              <p className="text-sm text-dt-parchment-dim">
                This ayah hasn't been analyzed yet — its iʿrāb will appear here once it has.
              </p>
            </div>
          ) : null
        ) : (
          !loading && (
            <div className="text-center text-sm text-dt-parchment-dim py-10">
              The analyzed sentence will appear here — every word tappable,
              colored by type.
            </div>
          )
        )}
      </div>

      {structureWord && (
        <WordMorphology
          key={`${structureWord.key}:${structureWord.morph?.label || ""}`}
          word={structureWord.morph}
          onClose={() => setStructureWord(null)}
          verifiable={isStaff && !!tokens?.[structureWord.key]}
          verified={!!tokens?.[structureWord.key]?.verified}
          onVerify={() =>
            verifyToken(structureWord.key, !tokens?.[structureWord.key]?.verified)
              .catch(() => {})
          }
        />
      )}
    </div>
  );
}
